import { forceCollide, forceSimulation, forceX, forceY } from 'd3';
import { isEmpty, sortBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { COLLISION_CONFIG } from '../../../helpers';

export type Position = Record<string, { x: number; y: number }>;

export interface ForceLabelNode {
  id: string;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  radius: number;
  type: 'label' | 'dot' | 'pointer';
  label?: string;
  width?: number;
  height?: number;
  fx?: number | null;
  fy?: number | null;
  stackId?: string;
  stackIndex?: number;
}

interface RadialCandidate {
  x: number;
  y: number;
  angle: number;
  radius: number;
  ring: number;
}

interface StackGroup {
  stackId: string;
  anchorX: number;
  anchorY: number;
  labels: ForceLabelNode[];
}

interface StackBounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  centerX: number;
  centerY: number;
}

const rectIntersectsCircle = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  cx: number,
  cy: number,
  r: number,
): boolean => {
  const closestX = Math.max(x1, Math.min(cx, x2));
  const closestY = Math.max(y1, Math.min(cy, y2));
  const dx = closestX - cx;
  const dy = closestY - cy;
  return dx * dx + dy * dy < r * r;
};

const pushRectOutsideCircle = (
  cx: number,
  cy: number,
  w: number,
  h: number,
  px: number,
  py: number,
  r: number,
) => {
  const dx = cx - px;
  const dy = cy - py;
  const dist = Math.hypot(dx, dy) || 1;
  const nx = dx / dist;
  const ny = dy / dist;
  const eps = 1.5;
  return {
    x: px + (r + Math.max(w, h) / 2 + eps) * nx,
    y: py + (r + Math.max(w, h) / 2 + eps) * ny,
  };
};

const groupLabelsByAnchor = (
  labels: ForceLabelNode[],
  tolerance = 0.5,
): Map<string, ForceLabelNode[]> => {
  const groups = new Map<string, ForceLabelNode[]>();

  for (const label of labels) {
    const keyX = Math.round(label.anchorX / tolerance) * tolerance;
    const keyY = Math.round(label.anchorY / tolerance) * tolerance;
    const key = `${keyX},${keyY}`;

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(label);
  }

  return groups;
};

const createStacks = (
  anchorGroups: Map<string, ForceLabelNode[]>,
): StackGroup[] => {
  const stacks: StackGroup[] = [];
  const { maxSize } = COLLISION_CONFIG.STACK;

  for (const [anchorKey, labels] of anchorGroups.entries()) {
    if (labels.length <= 1) {
      continue;
    }

    const [x, y] = anchorKey.split(',').map(Number);

    for (let i = 0; i < labels.length; i += maxSize) {
      const stackLabels = labels.slice(i, i + maxSize);
      const stackIndex = Math.floor(i / maxSize);

      stacks.push({
        stackId: `${anchorKey}-stack-${stackIndex}`,
        anchorX: x,
        anchorY: y,
        labels: stackLabels,
      });
    }
  }

  return stacks;
};

const layoutStack = (
  stack: StackGroup,
  offsetX: number,
  offsetY: number,
): void => {
  const { gap, preferredDirection } = COLLISION_CONFIG.STACK;
  const direction = preferredDirection === 'up' ? -1 : 1;

  let currentY = offsetY;

  for (let i = 0; i < stack.labels.length; i++) {
    const label = stack.labels[i];
    const height = label.height ?? 16;

    label.stackId = stack.stackId;
    label.stackIndex = i;
    label.fx = offsetX;
    label.fy = currentY;
    label.x = offsetX;
    label.y = currentY;

    currentY += direction * (height + gap);
  }
};

const positionStacks = (stacks: StackGroup[]): void => {
  const stacksByAnchor = new Map<string, StackGroup[]>();

  for (const stack of stacks) {
    const key = `${stack.anchorX},${stack.anchorY}`;
    if (!stacksByAnchor.has(key)) {
      stacksByAnchor.set(key, []);
    }
    stacksByAnchor.get(key)!.push(stack);
  }

  for (const [, anchorStacks] of stacksByAnchor.entries()) {
    if (anchorStacks.length === 1) {
      const stack = anchorStacks[0];
      layoutStack(stack, stack.anchorX, stack.anchorY - 30);
    } else {
      const { horizontalOffset } = COLLISION_CONFIG.STACK;
      const angleStep = (2 * Math.PI) / anchorStacks.length;

      anchorStacks.forEach((stack, index) => {
        const angle = index * angleStep;
        const offsetX = stack.anchorX + Math.cos(angle) * horizontalOffset;
        const offsetY = stack.anchorY + Math.sin(angle) * horizontalOffset - 20;
        layoutStack(stack, offsetX, offsetY);
      });
    }
  }
};

const getStackBounds = (stack: StackGroup): StackBounds | null => {
  if (stack.labels.length === 0) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const label of stack.labels) {
    const halfW = (label.width ?? 50) / 2;
    const halfH = (label.height ?? 16) / 2;

    minX = Math.min(minX, label.x - halfW);
    minY = Math.min(minY, label.y - halfH);
    maxX = Math.max(maxX, label.x + halfW);
    maxY = Math.max(maxY, label.y + halfH);
  }

  return {
    x1: minX,
    y1: minY,
    x2: maxX,
    y2: maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
};

const boundsOverlap = (a: StackBounds, b: StackBounds): boolean => {
  const overlapX = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
  const overlapY = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
  return overlapX > 0 && overlapY > 0;
};

const resolveStackCollisions = (stacks: StackGroup[]): void => {
  const maxIterations = 10;
  const padding = COLLISION_CONFIG.LABEL.padding * 2;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let hadCollision = false;

    for (let i = 0; i < stacks.length; i++) {
      for (let j = i + 1; j < stacks.length; j++) {
        const boundsA = getStackBounds(stacks[i]);
        const boundsB = getStackBounds(stacks[j]);

        if (!boundsA || !boundsB || !boundsOverlap(boundsA, boundsB)) {
          continue;
        }

        hadCollision = true;

        const dx = boundsA.centerX - boundsB.centerX || Math.random() - 0.5;
        const dy = boundsA.centerY - boundsB.centerY || Math.random() - 0.5;
        const dist = Math.hypot(dx, dy) || 1;

        const overlapX =
          Math.min(boundsA.x2, boundsB.x2) - Math.max(boundsA.x1, boundsB.x1);
        const overlapY =
          Math.min(boundsA.y2, boundsB.y2) - Math.max(boundsA.y1, boundsB.y1);
        const separation = (Math.max(overlapX, overlapY) + padding) / 2;

        const pushX = (dx / dist) * separation;
        const pushY = (dy / dist) * separation;

        for (const label of stacks[i].labels) {
          label.x += pushX;
          label.y += pushY;
          label.fx = label.x;
          label.fy = label.y;
        }

        for (const label of stacks[j].labels) {
          label.x -= pushX;
          label.y -= pushY;
          label.fx = label.x;
          label.fy = label.y;
        }
      }
    }

    if (!hadCollision) {
      break;
    }
  }
};

const avoidPointers = (stack: StackGroup, pointers: ForceLabelNode[]): void => {
  const bounds = getStackBounds(stack);
  if (!bounds) {
    return;
  }

  const { padding } = COLLISION_CONFIG.LABEL;

  for (const pointer of pointers) {
    const pr = pointer.radius + padding;

    if (
      !rectIntersectsCircle(
        bounds.x1,
        bounds.y1,
        bounds.x2,
        bounds.y2,
        pointer.x,
        pointer.y,
        pr,
      )
    ) {
      continue;
    }

    const dx = bounds.centerX - pointer.x;
    const dy = bounds.centerY - pointer.y;
    const dist = Math.hypot(dx, dy) || 1;

    const stackSize = Math.max(bounds.x2 - bounds.x1, bounds.y2 - bounds.y1);
    const pushDist = pr + stackSize / 2 + 2;

    const pushX = (dx / dist) * pushDist - dx;
    const pushY = (dy / dist) * pushDist - dy;

    for (const label of stack.labels) {
      label.x += pushX;
      label.y += pushY;
      label.fx = label.x;
      label.fy = label.y;
    }
  }
};

const getClusterDensity = (
  anchorX: number,
  anchorY: number,
  labels: ForceLabelNode[],
): number => {
  const CLUSTER_RADIUS = 70;
  return labels.filter(other => {
    const dx = other.anchorX - anchorX;
    const dy = other.anchorY - anchorY;
    return dx * dx + dy * dy <= CLUSTER_RADIUS * CLUSTER_RADIUS;
  }).length;
};

const makeRadialCandidates = (
  anchorX: number,
  anchorY: number,
  preferAngle: number,
  density: number,
): RadialCandidate[] => {
  const candidates: RadialCandidate[] = [];

  const ringConfig =
    density <= 4
      ? [{ radius: 30, slots: 8 }]
      : density <= 8
        ? [
            { radius: 30, slots: 8 },
            { radius: 55, slots: 12 },
          ]
        : [
            { radius: 30, slots: 8 },
            { radius: 55, slots: 12 },
            { radius: 80, slots: 16 },
          ];

  ringConfig.forEach((ring, ringIndex) => {
    const angleStep = (2 * Math.PI) / ring.slots;

    for (let i = 0; i < ring.slots; i++) {
      const angleOffset =
        i % 2 === 0
          ? Math.floor(i / 2) * angleStep
          : -Math.ceil(i / 2) * angleStep;

      const angle = preferAngle + angleOffset;
      candidates.push({
        x: anchorX + Math.cos(angle) * ring.radius,
        y: anchorY + Math.sin(angle) * ring.radius,
        angle,
        radius: ring.radius,
        ring: ringIndex + 1,
      });
    }
  });

  return candidates;
};

const sortCandidatesByPreference = (
  candidates: RadialCandidate[],
  preferAngle: number,
): RadialCandidate[] =>
  candidates.sort((a, b) => {
    const aDiff = Math.abs(
      ((a.angle - preferAngle + Math.PI) % (2 * Math.PI)) - Math.PI,
    );
    const bDiff = Math.abs(
      ((b.angle - preferAngle + Math.PI) % (2 * Math.PI)) - Math.PI,
    );

    if (Math.abs(aDiff - bDiff) > 0.2) {
      return aDiff - bDiff;
    }
    if (a.ring !== b.ring) {
      return a.ring - b.ring;
    }
    return a.radius - b.radius;
  });

const createSignature = (nodes: ForceLabelNode[]): string => {
  const labels = sortBy(
    nodes
      .filter(n => n.type === 'label')
      .map(n => [n.id, n.anchorX, n.anchorY, n.width ?? 0, n.height ?? 0]),
    arr => arr[0],
  );

  const pointers = sortBy(
    nodes
      .filter(n => n.type === 'pointer')
      .map(n => [n.id, n.x, n.y, n.radius]),
    arr => arr[0],
  );

  return JSON.stringify({ labels, pointers });
};

export const useLabelForceLayout = (nodes: ForceLabelNode[]) => {
  const [positions, setPositions] = useState<Position>({});
  const prevPositionsRef = useRef<Position>({});
  const prevSignatureRef = useRef<string>('');

  useEffect(() => {
    if (!nodes.length) {
      return;
    }

    const signature = createSignature(nodes);
    if (
      signature === prevSignatureRef.current &&
      !isEmpty(prevPositionsRef.current)
    ) {
      setPositions(prevPositionsRef.current);
      return;
    }
    prevSignatureRef.current = signature;

    // The layout and d3 mutate positions in place, so work on copies, never on the caller's nodes
    const workNodes = nodes.map(n => ({ ...n }));
    const pointers = workNodes.filter(n => n.type === 'pointer');
    const allLabels = workNodes.filter(n => n.type === 'label');

    if (allLabels.length === 0) {
      setPositions({});
      prevPositionsRef.current = {};
      return;
    }

    const anchorGroups = groupLabelsByAnchor(allLabels);
    const stacks = createStacks(anchorGroups);

    positionStacks(stacks);
    resolveStackCollisions(stacks);

    for (const stack of stacks) {
      avoidPointers(stack, pointers);
    }

    const stackedLabelIds = new Set(
      stacks.flatMap(s => s.labels.map(l => l.id)),
    );
    const nonStackedLabels = allLabels.filter(l => !stackedLabelIds.has(l.id));

    for (const n of workNodes) {
      if (n.type !== 'label') {
        n.fx = n.anchorX;
        n.fy = n.anchorY;
      }
    }

    for (const n of nonStackedLabels) {
      const prev = prevPositionsRef.current[n.id];
      if (prev) {
        n.x = prev.x;
        n.y = prev.y;
      }
    }

    const simNodes = [...pointers, ...nonStackedLabels];

    if (nonStackedLabels.length > 0) {
      const sim = forceSimulation<ForceLabelNode>(simNodes)
        .force(
          'collide',
          forceCollide<ForceLabelNode>()
            .radius(n =>
              n.type === 'label'
                ? (n.radius ?? 12) + COLLISION_CONFIG.LABEL.padding
                : 0,
            )
            .strength(1),
        )
        .force(
          'x',
          forceX(n => (n as ForceLabelNode).anchorX).strength(n =>
            (n as ForceLabelNode).type === 'label'
              ? COLLISION_CONFIG.LABEL.anchorStrength
              : 0,
          ),
        )
        .force(
          'y',
          forceY(n => (n as ForceLabelNode).anchorY).strength(n =>
            (n as ForceLabelNode).type === 'label'
              ? COLLISION_CONFIG.LABEL.anchorStrength
              : 0,
          ),
        )
        .alpha(0.6)
        .alphaDecay(1);

      for (
        let i = 0, N = Math.min(80, 6 * nonStackedLabels.length);
        i < N;
        i++
      ) {
        sim.tick();
      }
      sim.stop();
    }

    for (const label of nonStackedLabels) {
      const { width = 50, height = 16, anchorX, anchorY } = label;
      const halfW = width / 2;
      const halfH = height / 2;

      for (const other of allLabels) {
        if (other.id === label.id || !other.width || !other.height) {
          continue;
        }

        const ow = other.width / 2;
        const oh = other.height / 2;

        const overlapX = Math.max(
          0,
          Math.min(label.x + halfW, other.x + ow) -
            Math.max(label.x - halfW, other.x - ow),
        );
        const overlapY = Math.max(
          0,
          Math.min(label.y + halfH, other.y + oh) -
            Math.max(label.y - halfH, other.y - oh),
        );

        if (overlapX > 0 && overlapY > 0) {
          const dx = label.x - other.x || Math.random() - 0.5;
          const dy = label.y - other.y || Math.random() - 0.5;
          const dist = Math.hypot(dx, dy) || 1;
          const offset = (Math.max(overlapX, overlapY) / dist) * 0.5;
          label.x += dx * offset;
          label.y += dy * offset;
        }
      }

      for (const p of pointers) {
        const dx = label.x - p.x;
        const dy = label.y - p.y;
        const dist = Math.hypot(dx, dy);
        const radius = p.radius + COLLISION_CONFIG.LABEL.padding + 4;
        if (dist < radius) {
          const push = radius - dist + 2;
          label.x += (dx / (dist || 1)) * push;
          label.y += (dy / (dist || 1)) * push;
        }
      }

      const dx = label.x - anchorX;
      const dy = label.y - anchorY;
      const dist = Math.hypot(dx, dy);
      const minDist = (label.radius ?? 12) + COLLISION_CONFIG.LABEL.padding + 4;
      if (dist < minDist) {
        const push = minDist - dist + 2;
        label.x += (dx / (dist || 1)) * push;
        label.y += (dy / (dist || 1)) * push;
      }

      const maxDist =
        COLLISION_CONFIG.LABEL.maxDistance +
        (getClusterDensity(anchorX, anchorY, allLabels) > 8 ? 30 : 0);

      if (dist > maxDist) {
        const s = maxDist / dist;
        label.x = anchorX + dx * s;
        label.y = anchorY + dy * s;
      }
    }

    const out: Position = {};

    for (const label of nonStackedLabels) {
      const { anchorX, anchorY, width = 40, height = 14 } = label;

      const dx0 = label.x - anchorX;
      const dy0 = label.y - anchorY;
      const preferAngle = Math.atan2(dy0, dx0);
      const density = getClusterDensity(anchorX, anchorY, allLabels);

      const radialCandidates = makeRadialCandidates(
        anchorX,
        anchorY,
        preferAngle,
        density,
      );

      const sortedCandidates = sortCandidatesByPreference(
        radialCandidates,
        preferAngle,
      );

      let best = { x: label.x, y: label.y };
      let bestScore = Number.POSITIVE_INFINITY;
      let bestTouchedPointer = false;

      for (const candidate of sortedCandidates) {
        const lx1 = candidate.x - width / 2;
        const ly1 = candidate.y - height / 2;
        const lx2 = lx1 + width;
        const ly2 = ly1 + height;

        let overlap = 0;
        for (const other of allLabels) {
          if (other.id === label.id || !other.width || !other.height) {
            continue;
          }

          const ox1 = other.x - other.width / 2;
          const oy1 = other.y - other.height / 2;
          const ox2 = other.x + other.width / 2;
          const oy2 = other.y + other.height / 2;

          const oX = Math.max(0, Math.min(lx2, ox2) - Math.max(lx1, ox1));
          const oY = Math.max(0, Math.min(ly2, oy2) - Math.max(ly1, oy1));

          if (oX > 0 && oY > 0) {
            overlap += oX * oY;
            if (overlap >= bestScore) {
              break;
            }
          }
        }

        if (overlap >= bestScore) {
          continue;
        }

        let touchesPointer = false;
        for (const p of pointers) {
          const pr = p.radius + COLLISION_CONFIG.LABEL.padding;
          if (rectIntersectsCircle(lx1, ly1, lx2, ly2, p.x, p.y, pr)) {
            overlap += 9999;
            touchesPointer = true;
            break;
          }
        }

        const ringBias = candidate.ring * 2;
        const anchorBias = candidate.radius * 0.01;
        const score = overlap + ringBias + anchorBias;

        if (score < bestScore) {
          bestScore = score;
          best = { x: candidate.x, y: candidate.y };
          bestTouchedPointer = touchesPointer;
        }
      }

      if (bestTouchedPointer && pointers.length > 0) {
        let nearest = pointers[0];
        let dmin = Infinity;
        for (const p of pointers) {
          const d = Math.hypot(best.x - p.x, best.y - p.y);
          if (d < dmin) {
            dmin = d;
            nearest = p;
          }
        }
        best = pushRectOutsideCircle(
          best.x,
          best.y,
          width,
          height,
          nearest.x,
          nearest.y,
          nearest.radius + COLLISION_CONFIG.LABEL.padding,
        );
      }

      label.x = best.x;
      label.y = best.y;
      out[label.id] = { x: best.x, y: best.y };
    }

    for (const stack of stacks) {
      for (const label of stack.labels) {
        out[label.id] = { x: label.x, y: label.y };
      }
    }

    setPositions(out);
    prevPositionsRef.current = out;
  }, [nodes]);

  return positions;
};
